import {Directive, ElementRef, HostListener, Input} from '@angular/core';
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';

/**
 * Directive that applies currency masking to input elements using ngx-mask.
 * Formats numeric input with thousand separators and decimal places.
 *
 * @example
 * Basic usage with default settings (2 decimals, comma separator):
 * ```html
 * <input appCurrencyMask formControlName="amount" type="text"/>
 * ```
 *
 * @example
 * Custom decimal places and separator:
 * ```html
 * <input appCurrencyMask 
 *        [decimals]="2" 
 *        [separator]="','" 
 *        [decimal]="'.'" 
 *        formControlName="amount" 
 *        type="text"/>
 * ```
 *
 * @example
 * With currency prefix wrapper (recommended):
 * ```html
 * <div class="currency-input-wrapper">
 *   <span class="currency-prefix">R </span>
 *   <input appCurrencyMask 
 *          class="form-control currency-input"
 *          formControlName="amount" 
 *          type="text"/>
 * </div>
 * ```
 */
@Directive({
    selector: '[appCurrencyMask]',
    providers: [provideNgxMask()],
    standalone: true,
    hostDirectives: [
        {
            directive: NgxMaskDirective,
            inputs: ['mask', 'thousandSeparator', 'decimalMarker', 'dropSpecialCharacters']
        }
    ]
})
export class CurrencyMaskDirective
{
    /**
     * Number of decimal places to display. Defaults to 2.
     * @example
     * [decimals]="2" // Displays: 1,234.56
     * [decimals]="0" // Displays: 1,234
     */
    @Input() decimals: number = 2;

    /**
     * Character used as thousand separator. Defaults to comma.
     * @example
     * [separator]="','" // 1,234.56
     * [separator]="' '" // 1 234.56
     */
    @Input() separator: string = ',';

    /**
     * Character used as decimal marker. Defaults to period.
     * @example
     * [decimal]="'.'" // 1,234.56
     * [decimal]="','" // 1.234,56 (European format)
     */
    @Input() decimal: string = '.';

    /**
     * Whether to keep special characters (prefix, separators) in the form control value.
     * When false (default), returns clean numeric value. When true, returns formatted string.
     * @example
     * [keepSymbols]="false" // Value: "1234.56"
     * [keepSymbols]="true"  // Value: "1,234.56"
     */
    @Input() keepSymbols: boolean = false;

    constructor(private elementRef: ElementRef)
    {
    }

    /**
     * Computed mask value based on decimals configuration.
     * @returns Mask pattern string (e.g., "separator.2")
     */
    get maskValue(): string
    {
        return `separator.${this.decimals}`;
    }

    /**
     * Prevents deletion of the decimal separator when backspacing or deleting.
     * Only applies when decimals > 0 and the decimal separator exists in the value.
     */
    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void
    {
        if (this.decimals === 0)
        {
            return;
        }

        const input = this.elementRef.nativeElement as HTMLInputElement;
        const value = input.value;

        if (!value.includes(this.decimal))
        {
            return;
        }

        const selectionStart = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;

        if (event.key === 'Backspace' || event.key === 'Delete')
        {
            if (selectionStart !== selectionEnd)
            {
                const selectedText = value.substring(selectionStart, selectionEnd);
                if (selectedText.includes(this.decimal))
                {
                    event.preventDefault();
                    return;
                }
            }

            let positionToCheck: number;

            if (event.key === 'Backspace')
            {
                positionToCheck = selectionStart - 1;
            }
            else
            {
                positionToCheck = selectionStart;
            }

            if (positionToCheck >= 0 && positionToCheck < value.length && value[positionToCheck] === this.decimal)
            {
                event.preventDefault();
                return;
            }
        }
    }
}

